import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle, ArrowLeft, FlaskConical, Users, School,
  Mail, Phone, Shield, Zap, BarChart3, Gift, Star,
  Headphones, Mic, BookOpen, PenTool
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const GRADE_BANDS = [
  { id: "K-2", label: "K-2", desc: "Kindergarten through Grade 2" },
  { id: "3-5", label: "3-5", desc: "Grades 3 through 5" },
  { id: "6-8", label: "6-8", desc: "Grades 6 through 8" },
  { id: "9-12", label: "9-12", desc: "Grades 9 through 12" },
];

const JoinBeta = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    title: "",
    school_name: "",
    district: "",
    email: "",
    phone: "",
    grade_bands: [],
    estimated_ell_students: "",
    how_heard: "",
    message: "",
    // v6.34.13 — Moi-Mode pilot waitlist interest (optional, K-2 only)
    moi_mode_interest: false,
    moi_mode_grade_bands: [],
  });

  const toggleMoiGrade = (g) => {
    setForm((prev) => ({
      ...prev,
      moi_mode_grade_bands: prev.moi_mode_grade_bands.includes(g)
        ? prev.moi_mode_grade_bands.filter((x) => x !== g)
        : [...prev.moi_mode_grade_bands, g],
    }));
  };

  const toggleGradeBand = (id) => {
    setForm((prev) => ({
      ...prev,
      grade_bands: prev.grade_bands.includes(id)
        ? prev.grade_bands.filter((g) => g !== id)
        : [...prev.grade_bands, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.school_name.trim()) {
      toast.error("Please fill in your name, email, and school name.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/beta-signups`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="beta-signup-success">
        <div className="w-20 h-20 bg-blue-100 text-blue-600 flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={40} />
        </div>
        <h1 className="text-3xl sm:text-4xl font-black mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Application Received
        </h1>
        <p className="text-gray-600 text-lg mb-4">
          Thank you for joining the MOSAIC Pilot Waitlist. We'll review your application and notify you as soon as beta invitations open for your school. Most applicants hear back within <strong>48 hours</strong>.
        </p>
        <p className="text-gray-500 text-sm mb-2">
          Check your email for a confirmation. If approved, you'll receive your admin login credentials and full platform access through <strong>September 30, 2026</strong>.
        </p>
        <p className="text-gray-500 text-sm mb-8">
          In the meantime, try our free demo to see the platform in action.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Link to="/try-demo">
            <Button className="h-12 px-8 btn-sharp-primary" data-testid="beta-success-try-demo">
              Try Free Demo
            </Button>
          </Link>
          <Link to="/">
            <Button variant="outline" className="h-12 px-8 btn-sharp" data-testid="beta-success-home">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="control-room-layout" data-testid="join-beta-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-800 via-teal-700 to-emerald-900" />
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M20 20h20v20H20zM0 0h20v20H0z\'/%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="relative max-w-5xl mx-auto px-6 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 text-white px-4 py-1.5 text-sm font-bold mb-6 border border-white/20">
            <FlaskConical size={16} />
            PILOT WAITLIST · BETA OPENING SOON
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Be First When MOSAIC <span className="text-yellow-400">Opens to NYC Schools</span>
          </h1>
          <p className="text-xs tracking-widest uppercase text-emerald-200/60 font-medium mb-4" style={{ letterSpacing: '0.15em' }}>
            Multilingual Oral &amp; Written Student Assessment for Instructional Clarity
          </p>
          <p className="text-lg sm:text-xl text-emerald-100 max-w-3xl mx-auto mb-4">
            Spring 2027 is coming — WIDA replaces NYSESLAT and your students have never seen the format.
            We're building a <strong className="text-white">waitlist of NYC schools</strong> who want to be invited as soon as MOSAIC opens. The beta brings 441 culturally responsive practice 
            questions across all four language domains, K-12 — free access for pilot schools, direct influence on the product, and priority support.
          </p>
          <p className="text-sm text-emerald-200 max-w-2xl mx-auto italic">
            Note: MOSAIC access on NYC DOE infrastructure requires ERMA approval, which is in progress. Joining the waitlist secures your school's place — invitations roll out as approvals clear.
          </p>
        </div>
      </section>

      {/* What Pilot Waitlist Schools Will Get */}
      <section className="border-b-2 border-gray-200 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            What Pilot Schools Will Get When Beta Opens
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Gift, title: "Free Full Access", desc: "Complete platform access for your school through September 30, 2026 — all grade bands, all modalities, unlimited assessments.", color: "bg-yellow-100 text-yellow-700" },
              { icon: Star, title: "Shape the Product", desc: "Your feedback directly influences what we build next. Feature requests from beta schools get top priority.", color: "bg-purple-100 text-purple-700" },
              { icon: Zap, title: "Priority Support", desc: "Direct line to our team. Same-day response on issues, training sessions for your teachers, and onboarding assistance.", color: "bg-blue-100 text-blue-700" },
              { icon: BarChart3, title: "Real Assessment Data", desc: "Run actual language-proficiency assessments with your ELL students. Track growth across Listening, Speaking, Reading, and Writing.", color: "bg-green-100 text-green-700" },
              { icon: Shield, title: "FERPA Compliant", desc: "Enterprise-grade data privacy from day one. RBAC, audit logging, data retention policies, and ERMA-ready documentation.", color: "bg-red-100 text-red-700" },
              { icon: Users, title: "Founding School Status", desc: "Beta schools become founding partners. Early adopters receive priority pricing when the beta ends on September 30, 2026.", color: "bg-teal-100 text-teal-700" },
            ].map((item, i) => (
              <div key={i} className="border-2 border-gray-200 bg-white p-6" data-testid={`beta-benefit-${i}`}>
                <div className={`w-12 h-12 ${item.color} flex items-center justify-center mb-4`}>
                  <item.icon size={24} />
                </div>
                <h3 className="font-bold text-base mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="border-b-2 border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Platform Includes
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: Headphones, label: "Listening", detail: "Audio-based with Big Picture scenes", color: "bg-purple-100 text-purple-700" },
              { icon: Mic, label: "Speaking", detail: "Voice recording with STT", color: "bg-blue-100 text-blue-700" },
              { icon: BookOpen, label: "Reading", detail: "Adaptive text comprehension", color: "bg-green-100 text-green-700" },
              { icon: PenTool, label: "Writing", detail: "Proficiency rubric scoring", color: "bg-orange-100 text-orange-700" },
            ].map((m, i) => (
              <div key={i} className="border-2 border-gray-200 p-4 text-center">
                <div className={`w-10 h-10 ${m.color} flex items-center justify-center mx-auto mb-2`}>
                  <m.icon size={20} />
                </div>
                <div className="font-bold text-sm">{m.label}</div>
                <div className="text-xs text-gray-500 mt-1">{m.detail}</div>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-4 text-center">
            Plus: 441 multicultural questions, 7 grade bands (K-12), teacher grading tools, analytics dashboards, growth tracking, PDF reports, and more.
          </p>
        </div>
      </section>

      {/* Application Form */}
      <section className="bg-white">
        <div className="max-w-2xl mx-auto px-6 py-14">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Apply for the Beta
            </h2>
            <p className="text-gray-600">
              Fill out the form below and we'll review your application within 48 hours.
            </p>
          </div>

          {/* Beta Terms Notice */}
          <div className="bg-amber-50 border-2 border-amber-200 p-5 mb-8" data-testid="beta-terms-notice">
            <p className="font-bold text-sm text-amber-800 uppercase tracking-wide mb-2">Beta Program Terms</p>
            <ul className="text-sm text-amber-900 space-y-1.5">
              <li><strong>Access Period:</strong> Upon approval through September 30, 2026</li>
              <li><strong>Cost:</strong> Free for the duration of the beta</li>
              <li><strong>Includes:</strong> Full platform access — all grade bands, all modalities, Automated rubric support, print materials, growth reports</li>
              <li><strong>After Beta:</strong> Founding schools receive priority pricing to continue with a paid license</li>
              <li><strong>Approval:</strong> Applications are reviewed individually — submitting does not guarantee access</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="beta-signup-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold mb-2 block">Your Name *</Label>
                <Input
                  type="text"
                  placeholder="e.g., Maria Santos"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-12 border-2 rounded-none"
                  data-testid="beta-name"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold mb-2 block">Your Title / Role</Label>
                <Input
                  type="text"
                  placeholder="e.g., ELL Coordinator"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="h-12 border-2 rounded-none"
                  data-testid="beta-title"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold mb-2 block">School Name *</Label>
                <Input
                  type="text"
                  placeholder="e.g., PS 124 Yung Wing"
                  value={form.school_name}
                  onChange={(e) => setForm({ ...form, school_name: e.target.value })}
                  className="h-12 border-2 rounded-none"
                  data-testid="beta-school"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold mb-2 block">District</Label>
                <Input
                  type="text"
                  placeholder="e.g., District 1, NYCPS"
                  value={form.district}
                  onChange={(e) => setForm({ ...form, district: e.target.value })}
                  className="h-12 border-2 rounded-none"
                  data-testid="beta-district"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold mb-2 block">Email *</Label>
                <Input
                  type="email"
                  placeholder="you@school.nyc"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-12 border-2 rounded-none"
                  data-testid="beta-email"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold mb-2 block">Phone</Label>
                <Input
                  type="tel"
                  placeholder="(212) 555-0100"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="h-12 border-2 rounded-none"
                  data-testid="beta-phone"
                />
              </div>
            </div>

            {/* Grade Bands */}
            <div>
              <Label className="text-sm font-semibold mb-3 block">Which grade bands do you serve?</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {GRADE_BANDS.map((band) => (
                  <button
                    key={band.id}
                    type="button"
                    onClick={() => toggleGradeBand(band.id)}
                    className={`border-2 p-3 text-center transition-all ${
                      form.grade_bands.includes(band.id)
                        ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                        : "border-gray-200 hover:border-gray-400"
                    }`}
                    data-testid={`beta-grade-${band.id}`}
                  >
                    <div className="font-bold text-lg">{band.label}</div>
                    <div className="text-xs text-gray-500">{band.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block">Estimated ELL Students</Label>
              <Input
                type="text"
                placeholder="e.g., 150"
                value={form.estimated_ell_students}
                onChange={(e) => setForm({ ...form, estimated_ell_students: e.target.value })}
                className="h-12 border-2 rounded-none"
                data-testid="beta-ell-count"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block">How did you hear about MOSAIC?</Label>
              <Input
                type="text"
                placeholder="e.g., colleague, conference, web search"
                value={form.how_heard}
                onChange={(e) => setForm({ ...form, how_heard: e.target.value })}
                className="h-12 border-2 rounded-none"
                data-testid="beta-how-heard"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block">Anything else we should know?</Label>
              <Textarea
                placeholder="Current ELL tools you're using, specific pain points, questions about the beta..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={3}
                className="border-2 rounded-none"
                data-testid="beta-message"
              />
            </div>

            {/* v6.34.13 — Moi-Mode pilot waitlist interest (optional, K-2 only) */}
            <div
              className="border-2 border-indigo-200 bg-indigo-50/50 p-4 rounded-none"
              data-testid="moi-mode-waitlist-section"
            >
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.moi_mode_interest}
                  onChange={(e) => setForm({ ...form, moi_mode_interest: e.target.checked })}
                  className="mt-1 w-5 h-5 cursor-pointer"
                  data-testid="moi-mode-interest-checkbox"
                />
                <span>
                  <span className="block font-bold text-indigo-900">
                    Interested in Moi-Mode (K-2 mascot accommodation)
                  </span>
                  <span className="block text-sm text-slate-700 mt-1">
                    Optional. Moi-Mode adds a friendly mascot to the welcome / transition / completion screens of the
                    K-2 student exam (never during a live item) with pre-recorded audio greetings. Default OFF;
                    school-admin opt-in only; ERMA-acknowledgement gated. Check this box to be invited to the K-2
                    pilot rollout once it opens.
                  </span>
                </span>
              </label>
              {form.moi_mode_interest && (
                <div className="mt-4 pl-8" data-testid="moi-mode-grade-band-picker">
                  <div className="text-xs font-semibold text-slate-600 mb-2">
                    Which K-2 grades would you want to enable?
                  </div>
                  <div className="flex gap-2">
                    {["K", "1", "2"].map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => toggleMoiGrade(g)}
                        className={`px-4 py-1.5 text-sm font-semibold border-2 rounded-none transition-colors ${
                          form.moi_mode_grade_bands.includes(g)
                            ? "bg-indigo-600 text-white border-indigo-700"
                            : "bg-white text-slate-700 border-slate-300 hover:border-indigo-400"
                        }`}
                        data-testid={`moi-mode-grade-${g}-btn`}
                      >
                        Grade {g}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold border-2 border-emerald-700"
              style={{ borderRadius: 0 }}
              data-testid="beta-submit-btn"
            >
              {loading ? (
                <><div className="loading-spinner w-5 h-5 mr-2 border-white border-t-transparent" /> Submitting...</>
              ) : (
                <><FlaskConical size={20} className="mr-2" /> Apply for Beta Access</>
              )}
            </Button>

            <p className="text-xs text-gray-400 text-center">
              Your information is handled securely under our <Link to="/privacy" className="underline">Privacy Policy</Link>.
              We'll never share your data with third parties.
            </p>
          </form>
        </div>
      </section>

      {/* Already tried the demo? */}
      <section className="bg-gray-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-12 text-center">
          <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Haven't tried MOSAIC yet?
          </h3>
          <p className="text-gray-400 mb-6">
            Take our 5-minute free demo first — no account needed. See your 5-minute practice summary across the four modalities, then apply for the beta.
          </p>
          <Link to="/try-demo">
            <Button
              className="h-12 px-8 bg-yellow-400 text-gray-900 hover:bg-yellow-300 font-bold border-2 border-yellow-500"
              style={{ borderRadius: 0 }}
              data-testid="beta-try-demo-cta"
            >
              Try Free Demo First
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default JoinBeta;
